/**
 * DeviceBadge Component
 * 
 * Displays customer device and browser information in a clean, compact badge format.
 * Shows browser name, version, and operating system with appropriate icons.
 * 
 * Features:
 * - Auto-detects device type for appropriate icons
 * - Responsive design for mobile and desktop
 * - Hover tooltips with detailed information
 * - Clean, professional styling
 */

import React from 'react'
import { 
  Monitor, 
  Smartphone, 
  Tablet, 
  Globe,
  Info
} from 'lucide-react'

// Import the type using a workaround for module resolution issues
import * as Types from '../../../types'
type CustomerProfileType = Types.CustomerProfile

export interface DeviceBadgeProps {
  /** Customer profile containing device information */
  profile: CustomerProfileType
  /** Size variant for different use cases */
  size?: 'sm' | 'md' | 'lg'
  /** Show detailed tooltip on hover */
  showTooltip?: boolean
  /** Custom CSS classes */
  className?: string
}

/**
 * Get appropriate device icon based on device type
 */
function getDeviceIcon(deviceType?: string) {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return <Smartphone className="w-3 h-3" />
    case 'tablet':
      return <Tablet className="w-3 h-3" />
    case 'desktop':
    default:
      return <Monitor className="w-3 h-3" />
  }
}

/**
 * Get device type color scheme
 */
function getDeviceColors(deviceType?: string): { bg: string; text: string } {
  switch (deviceType?.toLowerCase()) {
    case 'mobile':
      return { bg: 'bg-blue-50', text: 'text-blue-700' }
    case 'tablet':
      return { bg: 'bg-purple-50', text: 'text-purple-700' }
    case 'desktop':
    default:
      return { bg: 'bg-gray-50', text: 'text-gray-700' }
  }
}

/**
 * Format browser information for display
 */
function formatBrowserInfo(profile: CustomerProfileType): string {
  const browser = profile.browser_name || 'Unknown Browser'
  const version = profile.browser_version ? 
    ` ${profile.browser_version.split('.')[0]}` : 
    ''
  
  return `${browser}${version}`
}

/**
 * Format operating system for display
 */
function formatOperatingSystem(profile: CustomerProfileType): string {
  return profile.operating_system || 'Unknown OS'
}

/**
 * DeviceBadge component for displaying customer device information
 */
export function DeviceBadge({
  profile,
  size = 'md',
  showTooltip = true,
  className = ''
}: DeviceBadgeProps) {
  
  const browserInfo = formatBrowserInfo(profile)
  const osInfo = formatOperatingSystem(profile)
  const deviceIcon = getDeviceIcon(profile.device_type)
  
  // Size-based styling
  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5',
    lg: 'text-base px-4 py-2 gap-2'
  }
  
  // Create detailed tooltip content
  const tooltipContent = showTooltip ? (
    <div className="space-y-1">
      <div className="font-medium">Device Information</div>
      <div className="text-xs space-y-0.5">
        <div><span className="font-medium">Browser:</span> {profile.browser_name} {profile.browser_version}</div>
        <div><span className="font-medium">OS:</span> {profile.operating_system}</div>
        <div><span className="font-medium">Device:</span> {profile.device_type || 'Unknown'}</div>
        {profile.screen_resolution && (
          <div><span className="font-medium">Screen:</span> {profile.screen_resolution}</div>
        )}
        {profile.language && (
          <div><span className="font-medium">Language:</span> {profile.language}</div>
        )}
      </div>
    </div>
  ) : null
  
  return (
    <div className="relative group">
      <div 
        className={`
          inline-flex items-center rounded-md bg-gray-100 text-gray-700
          border border-gray-200 font-medium
          ${sizeClasses[size]}
          ${className}
        `}
        title={showTooltip ? undefined : `${browserInfo} • ${osInfo}`}
      >
        {/* Device type icon */}
        <span className="text-gray-500">
          {deviceIcon}
        </span>
        
        {/* Browser and OS information */}
        <span className="truncate">
          {browserInfo} • {osInfo}
        </span>
        
        {/* Info icon for tooltip indicator */}
        {showTooltip && (
          <Info className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      
      {/* Detailed tooltip on hover */}
      {showTooltip && tooltipContent && (
        <div className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          hidden group-hover:block z-50
          bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg
          min-w-max max-w-xs
          before:content-[''] before:absolute before:top-full before:left-1/2 
          before:transform before:-translate-x-1/2 before:border-4
          before:border-transparent before:border-t-gray-900
        ">
          {tooltipContent}
        </div>
      )}
    </div>
  )
}

/**
 * Lightweight device badge for compact spaces
 */
export function CompactDeviceBadge({ profile }: { profile: CustomerProfileType }) {
  const deviceType = profile.device_type || 'desktop'
  const deviceIcon = getDeviceIcon(deviceType)
  const colors = getDeviceColors(deviceType)
  const browserName = profile.browser_name || 'Unknown'
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1 text-xs rounded px-2 py-0.5
        ${colors.bg} ${colors.text}
      `}
      title={`${browserName} on ${profile.operating_system || 'Unknown OS'}`}
    >
      <span className="opacity-70">
        {deviceIcon}
      </span>
      <span className="truncate max-w-16 capitalize">
        {deviceType}
      </span>
    </div>
  )
}

/**
 * Device type indicator for quick visual reference
 */
export function DeviceTypeIndicator({ 
  deviceType, 
  className = '' 
}: { 
  deviceType?: string
  className?: string 
}) {
  const icon = getDeviceIcon(deviceType)
  
  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <span className="text-gray-500">
        {icon}
      </span>
    </div>
  )
}


export default DeviceBadge